import { ExclamationTriangleIcon, UserMinusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useRef, useState } from "react";
import { useClickOutsideBlock } from "../../helpers/useClickOutside";
import { LoaderSpinner } from "../LoaderSpinner";

export const DisableAccessModal = ({
    isOpen,
    isLoading,
    user,
    cancelClick,
    submitClick,
}) => {
    const modalRef = useRef(null);
    const [reason, setReason] = useState("");
    useClickOutsideBlock(modalRef, cancelClick);
    if (!isOpen) return null;
    return (
        <div aria-hidden="true" className="overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-40 justify-center items-center w-full md:inset-0 h-[calc(100%)] max-h-full">
            <div className="bg-gray-700 top-0 right-0 left-0 fixed z-40 justify-center items-center w-full md:inset-0 h-[calc(100%)] max-h-full opacity-30" />
            <div ref={modalRef} className={`relative p-5 w-full max-w-lg max-h-full bg-white border top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 rounded-md`}>
                <div className="flex justify-end">
                    <button 
                        onClick={cancelClick}
                        className="inline-flex items-center p-1 text-primary absolute"
                    >
                        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                </div>
                <div className="flex flex-col gap-4 text-center sm:text-left">
                    <h2 className="text-lg leading-none font-semibold flex items-center gap-2">
                        <UserMinusIcon className="h-6 w-6 text-red-500"/>
                        <span>Disable User Access</span>
                    </h2>
                    <p className="text-gray-700/90 text-sm">
                        This will prevent {user?.name || ""} from accessing the system while preserving their data.
                    </p>
                </div>
                <div className="space-y-4 my-4">
                    <div className="relative w-full rounded-lg border px-4 py-3 text-sm grid gap-2 items-start text-gray-700/60 bg-gray-100">
                        <ExclamationTriangleIcon className="h-5 w-5 text-gray-700/60" />
                        <div className="text-gray-700/80 col-start-2 grid justify-items-start gap-1 text-sm">
                            <strong>{user?.name || ""}</strong>
                            <p>
                                will be immediately logged out and unable to access any system features. Their data and permissions will be preserved.
                            </p>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <label
                            className="flex items-center gap-2 text-sm leading-none font-medium select-none"
                            htmlFor="reason"
                        >
                            Reason (Optional)
                        </label>
                        <input
                            id="reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Enter reason for disabling access..."
                            className="placeholder:text-muted-foreground border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-2">
                    <button 
                        onClick={() => {
                            cancelClick()
                            setReason("");
                        }}
                        className="min-w-20 inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-white hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:w-auto sm:text-sm"
                    >
                        {isLoading ? <LoaderSpinner width="20" height="20" border="3" colorSpinner="gray"/> : "Cancel"}
                    </button>
                    <button 
                        onClick={() => {
                            if (!isLoading) {
                                submitClick(user?.id, reason);
                                setReason("");
                            }
                        }}
                        className="min-w-24 justify-center inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    >
                        {isLoading ? <LoaderSpinner width="20" height="20" border="3" colorSpinner="gray"/> : "Disable Access"}
                    </button>
                </div>
            </div>
        </div>
    )
}